import { useTheme } from '../../themes';
import { COLORS } from '../../themes/colors';

const HEX_COLOR_RE = /^#[0-9A-F]{6}$/i;
export const isHexColor = (x: string) => {
  return x.match(HEX_COLOR_RE);
};

export const COLOR_UNKNOWN = '#efdbff';

export const withAlpha = (hex: string, alphaHex: string) =>
  isHexColor(hex) ? `${hex}${alphaHex}` : hex;

// Picks a readable glyph foreground for an arbitrary (often dynamically
// cycled) background color — e.g. an actor's lane color can land on a light
// palette entry like yellow.w300, where a hardcoded white glyph would be
// invisible. Relative-luminance heuristic, not full WCAG APCA — good enough
// for a two-way icon foreground choice.
export const getReadableForeground = (
  hex: string,
  opts?: { dark?: string; light?: string }
) => {
  const dark = opts?.dark ?? '#272C30';
  const light = opts?.light ?? '#FFFFFF';
  if (!isHexColor(hex)) return light;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const toLinear = (c: number) => {
    const cs = c / 255;
    return cs <= 0.03928 ? cs / 12.92 : Math.pow((cs + 0.055) / 1.055, 2.4);
  };
  const luminance = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
  return luminance > 0.5 ? dark : light;
};

export const useColors = () => {
  return COLORS;
};

export const useColorsTrafficLight = () => {
  const theme = useTheme();
  return [
    theme.custom.colors.success.main,
    theme.custom.colors.pending.main,
    theme.custom.colors.error.main
  ];
};

export const useColorsSpectrumDanger = () => {
  return ['#f5222d', '#ff4d4f', '#ff7875', '#ffa39e', '#ffccc7', '#fff1f0'];
};

export const useColorsSpectrumWarning = () => {
  return ['#fa8c16', '#ffa940', '#ffc069', '#ffd591', '#ffe7ba', '#fff7e6'];
};

export const useColorsSpectrumSecondary = () => {
  return ['#eb2f96', '#f759ab', '#ff85c0', '#ffadd2', '#ffd6e7', '#fff0f6'];
};

export const useColorsSpectrumPrimary = () => {
  return ['#1890ff', '#40a9ff', '#69c0ff', '#91d5ff', '#bae7ff', '#e6f7ff'];
};

export const useColorsSpectrumUnknown = () => {
  return ['#722ed1', '#9254de', '#b37feb', '#d3adf7', '#efdbff', '#f9f0ff'];
};

export const useColorsSpectrumSuccess = () => {
  return ['#52c41a', '#73d13d', '#95de64', '#b7eb8f', '#d9f7be', '#f6ffed'];
};

export const useColorsSpectrumGrayscale = () => {
  return ['#595959', '#8c8c8c', '#bfbfbf', '#d9d9d9', '#e8e8e8', '#f5f5f5'];
};

export const useColors4 = () => {
  const theme = useTheme();
  return [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.custom.colors.pending.main,
    theme.custom.colors.success.main
  ];
};

export const useColorUnknown = () => {
  return COLOR_UNKNOWN;
};
